import React from 'react';
import styles from './RingPlayer.module.css';

// Mapping from pose id to the citation key in src/data/citations.js
// that best supports the rationale we tell the user. Used by the
// "Why this stretch" sheet on the IDLE screen so the link out points
// to a paper that actually backs the claim, not a generic citation.
export const POSE_CITATIONS = Object.freeze({
  'child-pose': 'bo-2018-breath-coactivation',
  'body-scan':  'neels-2016-awareness',
  'butterfly':  'bo-2018-breath-coactivation',
  'pf-drop':    'talasz-2010-diaphragm',
  'side-lying': 'bo-2018-breath-coactivation',
});

// Rationale string keys per pose. Each lives in src/locales/strings.js
// as a 2-3 sentence plain-language explanation of why the pose helps.
export const POSE_RATIONALE_KEYS = Object.freeze({
  'child-pose': 'rp_pose_rationale_child_pose',
  'body-scan':  'rp_pose_rationale_body_scan',
  'butterfly':  'rp_pose_rationale_butterfly',
  'pf-drop':    'rp_pose_rationale_pf_drop',
  'side-lying': 'rp_pose_rationale_side_lying',
});

// Side-view silhouettes for stretch / passive-release exercises that
// don't have an active up/down movement to ride the rail. The flower
// mast would be misleading on these (the body isn't lifting anything),
// so we swap the rail stage for a quiet pose illustration that holds
// the same vertical space.
const POSES = {
  'child-pose': (
    <svg viewBox="0 0 200 140" aria-hidden="true" focusable="false">
      <g fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
        <line x1="20" y1="120" x2="180" y2="120" strokeOpacity="0.35" />
        <path d="M48 120 C 60 116, 78 110, 96 102" />
        <path d="M48 120 C 56 96, 70 80, 96 76" />
        <circle cx="106" cy="74" r="10" />
        <path d="M116 80 C 138 90, 156 100, 172 110" />
      </g>
    </svg>
  ),
  'body-scan': (
    <svg viewBox="0 0 200 140" aria-hidden="true" focusable="false">
      <g fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
        <line x1="20" y1="100" x2="180" y2="100" strokeOpacity="0.35" />
        <circle cx="40" cy="86" r="10" />
        <path d="M50 92 L 150 92" />
        <path d="M150 92 L 168 88" />
        <path d="M50 96 L 96 96" />
        <path d="M96 96 L 104 100" />
      </g>
    </svg>
  ),
  'butterfly': (
    <svg viewBox="0 0 200 140" aria-hidden="true" focusable="false">
      <g fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
        <line x1="20" y1="118" x2="180" y2="118" strokeOpacity="0.35" />
        <circle cx="40" cy="100" r="9" />
        <path d="M49 104 L 110 104" />
        <path d="M110 104 L 130 78 L 150 104" />
        <path d="M110 104 L 130 130 L 150 104" />
      </g>
    </svg>
  ),
  'pf-drop': (
    <svg viewBox="0 0 200 140" aria-hidden="true" focusable="false">
      <g fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
        <line x1="20" y1="124" x2="180" y2="124" strokeOpacity="0.35" />
        <circle cx="100" cy="46" r="10" />
        <path d="M100 56 C 96 70, 90 84, 86 96" />
        <path d="M86 96 L 60 124" />
        <path d="M86 96 L 130 96" />
        <path d="M130 96 L 156 124" />
        <path d="M100 56 C 110 64, 116 72, 122 80" />
      </g>
    </svg>
  ),
  'side-lying': (
    <svg viewBox="0 0 200 140" aria-hidden="true" focusable="false">
      <g fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
        <line x1="20" y1="120" x2="180" y2="120" strokeOpacity="0.35" />
        <circle cx="46" cy="92" r="10" />
        <path d="M56 94 C 80 92, 110 90, 130 94" />
        <path d="M130 94 C 142 102, 138 112, 122 116" />
        <path d="M122 116 C 110 116, 96 112, 86 106" />
      </g>
    </svg>
  ),
};

export default function Pose({ pose, label, variant = 'stage' }) {
  const art = POSES[pose];
  if (!art) return null;
  const className = variant === 'preview' ? styles.posePreview : styles.pose;
  return (
    <div className={className} role="img" aria-label={label || pose}>
      {art}
    </div>
  );
}
